"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const rawCountries_1 = require("./data/rawCountries");
const rawTerritories_1 = require("./data/rawTerritories");
function getMask(prefix, dialCode, predefinedMask, defaultMask, alwaysDefaultMask) {
    if (!predefinedMask || alwaysDefaultMask) {
        return `${prefix + ''.padEnd(dialCode.length, '.')} ${defaultMask}`;
    }
    return `${prefix + ''.padEnd(dialCode.length, '.')} ${predefinedMask}`;
}
function initCountries(countries, enableAreaCodes, prefix, defaultMask, alwaysDefaultMask) {
    let hiddenAreaCodes = [];
    const enableAllCodes = enableAreaCodes === true;
    const initializedCountries = [].concat(...countries.map((country) => {
        const countryItem = {
            name: country[0],
            regions: country[1],
            iso2: country[2],
            countryCode: country[3],
            dialCode: country[3],
            format: getMask(prefix, country[3], country[4], defaultMask, alwaysDefaultMask),
            priority: country[5] || 0,
            mainCode: false,
            isAreaCode: false,
            areaCodeLength: 0,
            hasAreaCodes: false,
        };
        const areaItems = [];
        if (country[6]) {
            country[6].map((areaCode) => {
                const areaItem = Object.assign({}, countryItem);
                areaItem.dialCode = country[3] + areaCode;
                areaItem.isAreaCode = true;
                areaItem.areaCodeLength = areaCode.length;
                areaItems.push(areaItem);
                return areaItem;
            });
        }
        if (areaItems.length > 0) {
            countryItem.mainCode = true;
            if (enableAllCodes ||
                (enableAreaCodes.constructor.name === 'Array' && enableAreaCodes.includes(country[2]))) {
                countryItem.hasAreaCodes = true;
                return [countryItem, ...areaItems];
            }
            hiddenAreaCodes = hiddenAreaCodes.concat(areaItems);
            return [countryItem];
        }
        return [countryItem];
    }));
    return [initializedCountries, hiddenAreaCodes];
}
function extendUserContent(userContent, contentItemIndex, extendingObject, firstExtension) {
    if (extendingObject === null)
        return;
    const keys = Object.keys(extendingObject);
    const values = Object.values(extendingObject);
    keys.forEach((iso2, index) => {
        if (firstExtension) {
            return userContent.push([iso2, values[index]]);
        }
        const countryIndex = userContent.findIndex((arr) => arr[0] === iso2);
        if (countryIndex === -1) {
            const newUserContent = [iso2];
            newUserContent[contentItemIndex] = values[index];
            userContent.push(newUserContent);
        }
        else {
            userContent[countryIndex][contentItemIndex] = values[index];
        }
        return userContent;
    });
}
function initUserContent(masks, priority, areaCodes) {
    const userContent = [];
    extendUserContent(userContent, 1, masks, true);
    extendUserContent(userContent, 3, priority);
    extendUserContent(userContent, 2, areaCodes);
    return userContent;
}
function extendRawCountries(countries, userContent) {
    if (userContent.length === 0)
        return countries;
    return countries.map((o) => {
        const userContentIndex = userContent.findIndex((arr) => arr[0] === o[2]);
        if (userContentIndex === -1)
            return o;
        const userContentCountry = userContent[userContentIndex];
        if (userContentCountry[1])
            o[4] = userContentCountry[1];
        if (userContentCountry[3])
            o[5] = userContentCountry[3];
        if (userContentCountry[2])
            o[6] = userContentCountry[2];
        return o;
    });
}
class CountryData {
    constructor({ enableAreaCodes, enableTerritories, regions, onlyCountries, preferredCountries, excludeCountries, preserveOrder, masks, priority, areaCodes, localization, prefix, defaultMask, alwaysDefaultMask, }) {
        const userContent = initUserContent(masks, priority, areaCodes);
        const rawCountries = extendRawCountries(JSON.parse(JSON.stringify(rawCountries_1.default)), userContent);
        const rawTerritories = extendRawCountries(JSON.parse(JSON.stringify(rawTerritories_1.default)), userContent);
        let [initializedCountries, hiddenAreaCodes] = initCountries(rawCountries, enableAreaCodes, prefix, defaultMask, alwaysDefaultMask);
        if (enableTerritories) {
            const [initializedTerritories] = initCountries(rawTerritories, enableAreaCodes, prefix, defaultMask, alwaysDefaultMask);
            initializedCountries = this.sortTerritories(initializedTerritories, initializedCountries);
        }
        if (regions)
            initializedCountries = this.filterRegions(regions, initializedCountries);
        this._onlyCountries = this.localizeCountries(this.excludeCountries(this.getFilteredCountryList(onlyCountries, initializedCountries, preserveOrder.includes('onlyCountries')), excludeCountries), localization, preserveOrder.includes('onlyCountries'));
        this._preferredCountries =
            preferredCountries.length === 0
                ? []
                : this.localizeCountries(this.getFilteredCountryList(preferredCountries, initializedCountries, preserveOrder.includes('preferredCountries')), localization, preserveOrder.includes('preferredCountries'));
        this._hiddenAreaCodes = this.excludeCountries(this.getFilteredCountryList(onlyCountries, hiddenAreaCodes), excludeCountries);
    }
    filterRegions(regions, countries) {
        if (typeof regions === 'string') {
            const region = regions;
            return countries.filter((country) => {
                return country.regions.some((element) => {
                    return element === region;
                });
            });
        }
        return countries.filter((country) => {
            const matches = regions.map((region) => {
                return country.regions.some((element) => {
                    return element === region;
                });
            });
            return matches.some((el) => el);
        });
    }
    sortTerritories(initializedTerritories, initializedCountries) {
        const fullCountryList = [...initializedTerritories, ...initializedCountries];
        fullCountryList.sort((a, b) => {
            if (a.name < b.name) {
                return -1;
            }
            if (a.name > b.name) {
                return 1;
            }
            return 0;
        });
        return fullCountryList;
    }
    getFilteredCountryList(countryCodes, sourceCountryList, preserveOrder) {
        if (countryCodes.length === 0)
            return sourceCountryList;
        let filteredCountries;
        if (preserveOrder) {
            filteredCountries = countryCodes
                .map((countryCode) => {
                const country = sourceCountryList.find((cnt) => cnt.iso2 === countryCode);
                if (country)
                    return country;
                return null;
            })
                .filter((country) => country);
        }
        else {
            filteredCountries = sourceCountryList.filter((country) => {
                return countryCodes.some((element) => {
                    return element === country.iso2;
                });
            });
        }
        return filteredCountries;
    }
    localizeCountries(countries, localization, preserveOrder) {
        var _a, _b;
        for (let i = 0; i < countries.length; i++) {
            if ((localization === null || localization === void 0 ? void 0 : localization[(_a = countries[i]) === null || _a === void 0 ? void 0 : _a.iso2]) !== undefined) {
                countries[i].localName = localization[countries[i].iso2];
            }
            else if ((localization === null || localization === void 0 ? void 0 : localization[(_b = countries[i]) === null || _b === void 0 ? void 0 : _b.name]) !== undefined) {
                countries[i].localName = localization[countries[i].name];
            }
        }
        if (!preserveOrder) {
            countries.sort((a, b) => {
                if (a.localName < b.localName) {
                    return -1;
                }
                if (a.localName > b.localName) {
                    return 1;
                }
                return 0;
            });
        }
        return countries;
    }
    excludeCountries(onlyCountries, excludedCountries) {
        if (excludedCountries.length === 0) {
            return onlyCountries;
        }
        return onlyCountries.filter((country) => {
            return !excludedCountries.includes(country.iso2);
        });
    }
}
exports.default = CountryData;
